/**
 * The frame every student-facing pledge page sits in.
 *
 * Shared between the declaration form and the status page because they are
 * the same thing to a student — one address, one look — and two copies of the
 * header would drift the first time either is touched.
 *
 * Deliberately standalone chrome: no portal layout, no sidebar, no sign-in
 * affordance. The people reading these pages have no account and offering
 * them one is noise.
 */
export function PledgeShell({
  subtitle = "Graduating Students’ Declaration of Intent",
  width = "max-w-3xl",
  children,
}: {
  subtitle?: string;
  width?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 sm:py-12">
      <div className={`${width} mx-auto`}>
        <div className="text-center mb-6">
          {/* The Institute's own mark, not a stock icon. Served from the
              portal's public folder, the same file the sign-in page and the
              sidebar use. If it ever goes missing the heading below still
              names the Institute, so the header never collapses to nothing. */}
          <img
            src="/niqs-logo.png"
            alt="The Nigerian Institute of Quantity Surveyors"
            className="h-16 sm:h-20 w-auto mx-auto mb-3 object-contain"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">
            The Nigerian Institute of Quantity Surveyors
          </div>
          <h1 className="text-lg sm:text-xl font-bold text-slate-800 mt-1">{subtitle}</h1>
        </div>

        {children}

        <p className="text-center text-[11px] text-slate-400 mt-6 leading-relaxed">
          Your details are held on NIQS systems for the Graduate Pledge Ceremony and are not
          shared with third parties (Nigeria Data Protection Act 2023).
        </p>
      </div>
    </div>
  );
}

export function PledgeCard({
  children, className = "",
}: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Turn a refusal into something a student can act on.
 *
 * Rate limits answer with Laravel's bare "Too Many Attempts." — which reads
 * as a fault rather than as "wait a bit", and tells nobody what to do. The
 * API also reports an expired or broken request as a 500 carrying the real
 * reason in the body, so the TEXT is what has to be matched here, not the
 * status code.
 */
export function pledgeErrorMessage(raw: string | null | undefined, fallback: string): string {
  const message = String(raw ?? "").trim();

  if (/too many attempts/i.test(message)) {
    return "You have tried that several times in a row. Please wait a few minutes and try again \u2014 "
      + "nothing you have already sent has been lost.";
  }
  if (/unauthenticated/i.test(message)) {
    return "That link is no longer valid. Use the link in your email, or ask for a new one.";
  }

  return message || fallback;
}
